import { GS, parseGs1 } from '@zenarty/shared';
import { ScanBarcode } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge, Callout, Card, PageHeader } from '../components/kit';

const SAMPLES: [string, string][] = [
  ['Pallet label (SSCC)', '(00)106141411234567897'],
  ['Case with lot and expiry', '(01)10614141000415(10)LOT-2481(17)270630(37)24'],
  ['Catch-weight case', '(01)10614141000422(3102)012500(10)B77'],
  ['Raw scanner output', `]C10110614141000415172706301024A1${'<GS>'}3712`],
];

/** Scanners send FNC1 as an invisible GS (0x1D); let people type it as <GS>, | or \x1d. */
const normalise = (s: string) => s.replace(/<GS>|\\x1d|\|/gi, GS);

export function BarcodePage() {
  const [input, setInput] = useState(SAMPLES[1]![1]);
  const result = useMemo(() => (input.trim() ? parseGs1(normalise(input.trim())) : null), [input]);
  return (
    <div className="stack" style={{ maxWidth: 980 }}>
      <PageHeader
        title="Barcode decoder"
        description="Decode GS1-128 and GS1 DataMatrix barcodes exactly as the scanners will: SSCC, GTIN, lot, expiry, serial, counts and catch weight, with check-digit and date validation."
      />
      <Card
        title="Scan or paste a barcode"
        sub="Type FNC1 separators as <GS> or |. Bracketed (AI) text also works."
      >
        <div className="stack">
          <textarea
            className="textarea mono"
            rows={3}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            aria-label="Barcode data"
          />
          <div className="row">
            <span className="small muted">Examples:</span>
            {SAMPLES.map(([label, value]) => (
              <button
                key={label}
                type="button"
                className="tag"
                style={{ cursor: 'pointer' }}
                onClick={() => setInput(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Card>
      {result ? (
        <>
          {result.ok ? (
            <Callout tone="ok" title="Valid GS1 data" />
          ) : (
            <Callout tone="danger" title="Problems found">
              <ul>
                {result.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Callout>
          )}
          {result.elements.length ? (
            <Card title="Decoded fields" flush>
              <table className="table">
                <thead>
                  <tr>
                    <th>AI</th>
                    <th>Meaning</th>
                    <th>Value</th>
                    <th>Raw</th>
                  </tr>
                </thead>
                <tbody>
                  {result.elements.map((e, i) => (
                    <tr key={`${e.ai}-${i}`}>
                      <td>
                        <span className="code-pill">({e.ai})</span>
                      </td>
                      <td>{e.title}</td>
                      <td>
                        <strong className="mono">
                          {e.value}
                          {e.unit ? ` ${e.unit}` : ''}
                        </strong>
                      </td>
                      <td className="mono muted">{e.raw}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
          <div className="row">
            {result.sscc ? <Badge tone="accent">Pallet / case SSCC</Badge> : null}
            {result.gtin ? <Badge tone="info">Trade item</Badge> : null}
            {result.lot ? <Badge>Lot {result.lot}</Badge> : null}
            {result.expiry ? <Badge tone="warn">Expires {result.expiry}</Badge> : null}
            {result.netWeightGrams ? (
              <Badge>{(result.netWeightGrams / 1000).toFixed(2)} kg</Badge>
            ) : null}
            {!result.elements.length ? (
              <span className="muted">
                <ScanBarcode size={14} /> Nothing decoded yet
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
